import { AssessmentSet } from '@lyrahealth-inc/shared-app-logic/src/models/assessmentSet/AssessmentSet'
import { Curriculum, Episode, EpisodeSummary } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProviderTaskType } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { ALL_CONVERSATION_TAGS, baseApi, RTKQueryTag } from '../../../data/baseApi'

type GetAllClientEpisodesArgs = {
  patientId: string
}

type GetClientEpisodesArgs = {
  patientId: string
  providerId: string
}

type GetClosedEpisodesArgs = {
  startDate: string
  endDate: string
  providerId: string
}

type GetEpisodeArgs = {
  episodeId: string
}

type GetCareTeamSlotsArgs = {
  episodeId: string
}

export type CareTeamSlotAssignment = {
  care_team_slot_assignment_id: string
  provider_lyra_id: string
}

export type CareTeamSlot = {
  care_team_slot: string
  display_name: string
  max_assignees: number
  license_required: boolean
  eligible_provider_roles: {
    virtual: string[]
    in_person: string[]
  }
  assignments: CareTeamSlotAssignment[]
}

export type CareTeamSlotsResponse = {
  episode_id: string
  slots: CareTeamSlot[]
}

type AssignCareTeamSlotsArgs = {
  episodeId: string
  providerLyraId: string
  careTeamSlots: string[]
}

type EndCareTeamAssignmentArgs = {
  episodeId: string
  careTeamSlotAssignmentId: string
}

type ReassignCareTeamAssignmentArgs = {
  episodeId: string
  careTeamSlotAssignmentId: string
  newProviderLyraId: string
}

type GetEpisodeParticipantsArgs = {
  providerId: string
}

export type EpisodeParticipantRosterParticipant = {
  lyra_id: string
  type: 'patient' | 'provider'
  role: string
  first_name: string | null
  last_name: string | null
}

export type EpisodeParticipantRoster = {
  anchor_patient_lyra_id: string
  participants: EpisodeParticipantRosterParticipant[]
}

type UpdateEpisodeArgs = {
  id: string
} & Partial<Episode>

type UpdateEpisodeRecordingConsentArgs = {
  id: string
  recording_consent: boolean
}

type UpdateCurriculumArgs = {
  id: string
  curriculum: Curriculum[]
}

type AssignCurriculumArgs = {
  id: string
}

type UpdateAssessmentTrackArgs = {
  id: string
  assessment_set_id: string
}

type RiskFlag = {
  id: string
  episode_id: string
  flag_type: string
  activated_by: string
}

type GetRiskFlagArgs = {
  episodeId: string
}

type ActivateRiskFlagArgs = {
  episodeId: string
  flagType: ProviderTaskType
}

type DeactivateRiskFlagArgs = {
  episodeId: string
}

type GetAssessmentTracksByProgramArgs = {
  programId: string
}

const episodesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllClientEpisodes: builder.query<Episode[], GetAllClientEpisodesArgs>({
      query: ({ patientId }) => ({
        method: 'get',
        url: `/lt/v1/patient/${patientId}/episodes`,
      }),
      providesTags: [RTKQueryTag.EPISODE],
    }),
    getClientEpisodes: builder.query<Episode[], GetClientEpisodesArgs>({
      query: ({ patientId, providerId }) => ({
        method: 'get',
        url: '/lt/v1/episodes',
        params: { patient_id: patientId, provider_id: providerId },
      }),
      providesTags: [RTKQueryTag.EPISODE],
    }),
    getEpisodeParticipants: builder.query<EpisodeParticipantRoster[], GetEpisodeParticipantsArgs>({
      query: ({ providerId }) => ({
        method: 'get',
        url: '/lt/v1/episode_participants',
        params: { provider_id: providerId },
        showAlert: false,
      }),
      providesTags: [RTKQueryTag.EPISODE],
    }),
    getCareTeamSlots: builder.query<CareTeamSlotsResponse, GetCareTeamSlotsArgs>({
      query: ({ episodeId }) => ({
        method: 'get',
        url: `/lt/v1/episodes/${episodeId}/care-team/slots`,
        showAlert: false,
      }),
      providesTags: [RTKQueryTag.EPISODE],
    }),
    assignCareTeamSlots: builder.mutation<void, AssignCareTeamSlotsArgs>({
      query: ({ episodeId, providerLyraId, careTeamSlots }) => ({
        method: 'post',
        url: `/lt/v1/episodes/${episodeId}/care-team/assignments`,
        data: { provider_lyra_id: providerLyraId, care_team_slots: careTeamSlots },
        showAlert: false,
      }),
    }),
    endCareTeamAssignment: builder.mutation<void, EndCareTeamAssignmentArgs>({
      query: ({ episodeId, careTeamSlotAssignmentId }) => ({
        method: 'delete',
        url: `/lt/v1/episodes/${episodeId}/care-team/assignments/${careTeamSlotAssignmentId}`,
        showAlert: false,
      }),
    }),
    reassignCareTeamAssignment: builder.mutation<void, ReassignCareTeamAssignmentArgs>({
      query: ({ episodeId, careTeamSlotAssignmentId, newProviderLyraId }) => ({
        method: 'post',
        url: `/lt/v1/episodes/${episodeId}/care-team/assignments/${careTeamSlotAssignmentId}/reassign`,
        data: { new_provider_lyra_id: newProviderLyraId },
        showAlert: false,
      }),
    }),
    getClosedEpisodes: builder.query<{ [key: string]: EpisodeSummary }, GetClosedEpisodesArgs>({
      query: ({ startDate, endDate, providerId }) => ({
        method: 'get',
        url: '/v1/providers/episodes/closed',
        params: { startDate, endDate, providerId },
      }),
      providesTags: [RTKQueryTag.EPISODE],
    }),
    getEpisode: builder.query<Episode, GetEpisodeArgs>({
      query: ({ episodeId }) => ({
        method: 'get',
        url: `/lt/v1/episodes/${episodeId}`,
      }),
      providesTags: [RTKQueryTag.EPISODE],
    }),
    updateEpisode: builder.mutation<Episode, UpdateEpisodeArgs>({
      query: ({ id, ...data }) => ({
        method: 'put',
        url: `/lt/v1/episodes/${id}`,
        data,
      }),
      invalidatesTags: [RTKQueryTag.EPISODE, ...ALL_CONVERSATION_TAGS],
    }),
    updateEpisodeRecordingConsent: builder.mutation<Episode, UpdateEpisodeRecordingConsentArgs>({
      query: ({ id, recording_consent }) => ({
        method: 'patch',
        url: `/lt/v1/episodes/${id}`,
        data: { recording_consent },
      }),
      invalidatesTags: [RTKQueryTag.EPISODE, ...ALL_CONVERSATION_TAGS],
    }),
    updateCurriculum: builder.mutation<Episode, UpdateCurriculumArgs>({
      query: ({ id, curriculum }) => ({
        method: 'patch',
        url: `/lt/v1/episodes/${id}`,
        data: { curriculum },
      }),
      invalidatesTags: [RTKQueryTag.EPISODE, RTKQueryTag.ASSIGNMENT],
    }),
    assignCurriculum: builder.mutation<void, AssignCurriculumArgs>({
      query: ({ id }) => ({
        method: 'post',
        url: `/lt/v1/episode/${id}/assignCurriculum`,
      }),
      invalidatesTags: [RTKQueryTag.EPISODE, RTKQueryTag.ASSIGNMENT],
    }),
    updateAssessmentTrack: builder.mutation<Episode, UpdateAssessmentTrackArgs>({
      query: ({ id, assessment_set_id }) => ({
        method: 'patch',
        url: `/lt/v1/episodes/${id}`,
        data: { assessment_set_id },
      }),
      invalidatesTags: [RTKQueryTag.EPISODE, RTKQueryTag.ASSIGNMENT, ...ALL_CONVERSATION_TAGS],
    }),
    getAssessmentTracksByProgram: builder.query<AssessmentSet[], GetAssessmentTracksByProgramArgs>({
      query: ({ programId }) => ({
        method: 'get',
        url: '/lt/v1/assessmentSets',
        params: { program_id: programId },
      }),
    }),
    activateRiskFlag: builder.mutation<RiskFlag, ActivateRiskFlagArgs>({
      query: ({ episodeId, flagType }) => ({
        method: 'post',
        url: `/lt/v1/episodes/${episodeId}/risk_flag`,
        data: { flag_type: flagType },
      }),
      invalidatesTags: (_result, _error, { episodeId }) => [{ type: RTKQueryTag.RISK_FLAG, id: episodeId }],
    }),
    getRiskFlag: builder.query<RiskFlag, GetRiskFlagArgs>({
      query: ({ episodeId }) => ({
        method: 'get',
        url: `/lt/v1/episodes/${episodeId}/risk_flag`,
        showAlert: false,
      }),
      providesTags: (_result, _error, { episodeId }) => [{ type: RTKQueryTag.RISK_FLAG, id: episodeId }],
    }),
    deactivateRiskFlag: builder.mutation<void, DeactivateRiskFlagArgs>({
      query: ({ episodeId }) => ({
        method: 'delete',
        url: `/lt/v1/episodes/${episodeId}/risk_flag`,
      }),
      invalidatesTags: (_result, _error, { episodeId }) => [
        { type: RTKQueryTag.RISK_FLAG, id: episodeId },
        RTKQueryTag.PROVIDER_TASK,
      ],
    }),
  }),
})

export const {
  useGetAllClientEpisodesQuery,
  useLazyGetAllClientEpisodesQuery,
  useGetClientEpisodesQuery,
  useLazyGetClientEpisodesQuery,
  useGetEpisodeParticipantsQuery,
  useGetCareTeamSlotsQuery,
  useAssignCareTeamSlotsMutation,
  useEndCareTeamAssignmentMutation,
  useReassignCareTeamAssignmentMutation,
  useGetClosedEpisodesQuery,
  useGetEpisodeQuery,
  useLazyGetEpisodeQuery,
  useUpdateEpisodeMutation,
  useUpdateEpisodeRecordingConsentMutation,
  useUpdateCurriculumMutation,
  useAssignCurriculumMutation,
  useUpdateAssessmentTrackMutation,
  useGetAssessmentTracksByProgramQuery,
  useActivateRiskFlagMutation,
  useGetRiskFlagQuery,
  useDeactivateRiskFlagMutation,
} = episodesApi
export default episodesApi
